import React, { useEffect, useMemo, useState } from "react";
import AdminPageFrame from "../components/admin/AdminPageFrame";
import config from "../config/config";
import { buildAuthFetchOptions } from "../utils/authSession";
import "../styles/admin-news.css";

const CATEGORY_OPTIONS = ["Alle", "Welt", "Europa", "Deutschland", "Frankreich", "Österreich"];
const STATUS_OPTIONS = [
  { value: "", label: "Alle Status" },
  { value: "draft", label: "Entwürfe" },
  { value: "published", label: "Veröffentlicht" },
  { value: "archived", label: "Archiviert" },
  { value: "rejected", label: "Abgelehnt" },
  { value: "discarded", label: "Verworfen" },
];

function formatDate(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (_error) {
    return "";
  }
}

async function fetchJson(url, options = {}) {
  const response = await fetch(url, buildAuthFetchOptions(options));
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.error || "Anfrage fehlgeschlagen.");
  }
  return payload;
}

function normalizeEditor(article) {
  if (!article) return null;
  return {
    id: article.id,
    title: article.title || "",
    excerpt: article.excerpt || "",
    body: article.body || "",
    category: article.category || "Alle",
    status: article.status || "draft",
    sourceList: Array.isArray(article.sourceList) ? article.sourceList : [],
    sourceCount: article.sourceCount || 0,
    viewCount: article.viewCount || 0,
    isAiArticle: Boolean(article.isAiArticle),
    searchTerm: article.searchTerm || "",
    publishedAt: article.publishedAt || "",
    updatedAt: article.updatedAt || "",
  };
}

export default function AdminNewsPage() {
  const [articles, setArticles] = useState([]);
  const [termCloud, setTermCloud] = useState([]);
  const [researchTerm, setResearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [results, setResults] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [articleQuery, setArticleQuery] = useState("");
  const [editor, setEditor] = useState(null);
  const [categories, setCategories] = useState(CATEGORY_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [researchLoading, setResearchLoading] = useState(false);
  const [savingAction, setSavingAction] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const selectedCount = selectedSources.length;

  async function loadAdminNews(currentStatus = statusFilter, currentQuery = articleQuery) {
    const url = new URL(`${config.API_BASE_URL}/api/news/admin/articles`);
    if (currentStatus) url.searchParams.set("status", currentStatus);
    if (currentQuery.trim()) url.searchParams.set("q", currentQuery.trim());

    const [articlePayload, cloudPayload] = await Promise.all([
      fetchJson(url.toString()),
      fetchJson(`${config.API_BASE_URL}/api/news/admin/term-cloud`),
    ]);

    setArticles(Array.isArray(articlePayload?.items) ? articlePayload.items : []);
    setCategories(
      Array.isArray(articlePayload?.categories) && articlePayload.categories.length
        ? articlePayload.categories
        : CATEGORY_OPTIONS
    );
    setTermCloud(Array.isArray(cloudPayload?.items) ? cloudPayload.items : []);
  }

  useEffect(() => {
    document.title = "Nachrichten | Admin | Experts4Europe";
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    loadAdminNews()
      .then(() => {
        if (mounted) setLoading(false);
      })
      .catch((loadError) => {
        if (!mounted) return;
        setError(loadError.message || "Admin-Nachrichten konnten nicht geladen werden.");
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const articleStats = useMemo(() => {
    return {
      total: articles.length,
      published: articles.filter((item) => item.status === "published").length,
      drafts: articles.filter((item) => item.status === "draft").length,
      views: articles.reduce((sum, item) => sum + Number(item.viewCount || 0), 0),
    };
  }, [articles]);

  const filteredResults = useMemo(() => {
    if (!researchTerm.trim()) return results;
    const lowered = researchTerm.trim().toLowerCase();
    return results.filter((item) => {
      const haystack = `${item.title || ""} ${item.summary || ""} ${item.sourceName || ""} ${item.note || ""}`.toLowerCase();
      return haystack.includes(lowered);
    });
  }, [results, researchTerm]);

  function toggleSourceSelection(source) {
    setSelectedSources((current) => {
      const exists = current.some((entry) => entry.id === source.id);
      if (exists) return current.filter((entry) => entry.id !== source.id);
      return [...current, source];
    });
  }

  async function handleResearchSubmit(event) {
    event.preventDefault();
    setResearchLoading(true);
    setError("");
    setMessage("");
    try {
      const payload = await fetchJson(`${config.API_BASE_URL}/api/news/admin/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          term: researchTerm,
          startDate,
          endDate,
        }),
      });
      setResults(Array.isArray(payload?.items) ? payload.items : []);
      setSelectedSources([]);
      setMessage("Recherche abgeschlossen.");
    } catch (researchError) {
      setError(researchError.message || "Die Recherche konnte nicht durchgeführt werden.");
    } finally {
      setResearchLoading(false);
    }
  }

  async function handleSourceAction(source, action) {
    setSavingAction(`${action}:${source.id}`);
    setError("");
    setMessage("");
    try {
      if (action === "ai") {
        const payload = await fetchJson(`${config.API_BASE_URL}/api/news/admin/ai-article`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            term: researchTerm,
            sources: [source],
          }),
        });
        setEditor(normalizeEditor(payload?.item));
        await loadAdminNews();
        setMessage("KI-Artikel als Entwurf angelegt.");
      } else {
        await fetchJson(`${config.API_BASE_URL}/api/news/admin/import-source`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: action === "archive" ? "archived" : "discarded",
            source,
            term: researchTerm,
          }),
        });
        await loadAdminNews();
        setMessage(action === "archive" ? "Quelle archiviert." : "Quelle verworfen.");
      }
    } catch (actionError) {
      setError(actionError.message || "Aktion konnte nicht ausgeführt werden.");
    } finally {
      setSavingAction("");
    }
  }

  async function handleCreateAiArticle() {
    if (!selectedSources.length) return;
    setSavingAction("create-ai");
    setError("");
    setMessage("");
    try {
      const payload = await fetchJson(`${config.API_BASE_URL}/api/news/admin/ai-article`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          term: researchTerm,
          sources: selectedSources,
        }),
      });
      setEditor(normalizeEditor(payload?.item));
      await loadAdminNews();
      setMessage("Neuer KI-Artikel angelegt.");
    } catch (actionError) {
      setError(actionError.message || "KI-Artikel konnte nicht angelegt werden.");
    } finally {
      setSavingAction("");
    }
  }

  async function handleArticleSearch(event) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await loadAdminNews(statusFilter, articleQuery);
    } catch (loadError) {
      setError(loadError.message || "Artikel konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }

  async function runArticleAction(action, article = editor) {
    if (!article?.id) return;
    setSavingAction(`${action}:${article.id}`);
    setError("");
    setMessage("");
    try {
      let payload = null;

      if (action === "save") {
        payload = await fetchJson(`${config.API_BASE_URL}/api/news/admin/articles/${article.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: article.title,
            excerpt: article.excerpt,
            body: article.body,
            category: article.category,
            status: article.status,
          }),
        });
      }

      if (action === "accept") {
        payload = await fetchJson(`${config.API_BASE_URL}/api/news/admin/articles/${article.id}/accept`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            category: article.category,
          }),
        });
      }

      if (action === "reject") {
        payload = await fetchJson(`${config.API_BASE_URL}/api/news/admin/articles/${article.id}/reject`, {
          method: "POST",
        });
      }

      if (action === "archive") {
        payload = await fetchJson(`${config.API_BASE_URL}/api/news/admin/articles/${article.id}/archive`, {
          method: "POST",
        });
      }

      if (action === "regenerate") {
        payload = await fetchJson(`${config.API_BASE_URL}/api/news/admin/articles/${article.id}/regenerate`, {
          method: "POST",
        });
      }

      if (action === "delete") {
        await fetchJson(`${config.API_BASE_URL}/api/news/admin/articles/${article.id}`, {
          method: "DELETE",
        });
        setEditor(null);
      } else {
        setEditor(normalizeEditor(payload?.item));
      }

      await loadAdminNews(statusFilter, articleQuery);
      setMessage(
        action === "save"
          ? "Bericht gespeichert."
          : action === "accept"
            ? "Bericht angenommen und veröffentlicht."
            : action === "reject"
              ? "Bericht abgelehnt."
              : action === "archive"
                ? "Bericht archiviert."
                : action === "regenerate"
                  ? "Bericht neu durch KI erstellt."
                  : "Bericht gelöscht."
      );
    } catch (actionError) {
      setError(actionError.message || "Aktion konnte nicht abgeschlossen werden.");
    } finally {
      setSavingAction("");
    }
  }

  return (
    <AdminPageFrame
      title="Nachrichten"
      subtitle="Recherche, redaktionelle Einordnung und Veröffentlichung laufen in einer gemeinsamen Nachrichtenoberfläche."
      badge="Redaktion & Recherche"
      contentClassName="admin-news-page"
      shellProps={{ liveLabel: "Nachrichten" }}
    >
      <div className="admin-news">
        <section className="admin-core-surface admin-news__workspace">
            <div className="admin-core-section-head">
              <div>
                <span className="admin-core-section-kicker">Operations Workspace</span>
                <h2 className="admin-core-section-title">Nachrichten</h2>
                <p className="admin-core-section-text">
                  Recherche, redaktionelle Einordnung und Veröffentlichung laufen in einer gemeinsamen
                  Nachrichtenoberfläche.
                </p>
              </div>
            </div>

            <section className="admin-core-summary-grid admin-news__summary">
              <article className="admin-core-stat-card admin-news__summary-card">
                <span>Berichte gesamt</span>
                <strong>{articleStats.total}</strong>
                <p>Alle verwalteten Nachrichtenobjekte in der Redaktion.</p>
              </article>
              <article className="admin-core-stat-card admin-news__summary-card">
                <span>Veröffentlicht</span>
                <strong>{articleStats.published}</strong>
                <p>Bereits freigegebene Berichte auf den Public Pages.</p>
              </article>
              <article className="admin-core-stat-card admin-news__summary-card">
                <span>Entwürfe</span>
                <strong>{articleStats.drafts}</strong>
                <p>Artikel in Bearbeitung oder vor der redaktionellen Entscheidung.</p>
              </article>
              <article className="admin-core-stat-card admin-news__summary-card">
                <span>Aufrufe gesamt</span>
                <strong>{articleStats.views}</strong>
                <p>Sichtbarkeit aller veröffentlichten Berichte in der Summe.</p>
              </article>
            </section>

            {message ? <div className="admin-news__notice admin-news__notice--success">{message}</div> : null}
            {error ? <div className="admin-news__notice admin-news__notice--error">{error}</div> : null}

            <div className="admin-news__stack">
              <section className="admin-news__panel admin-news__panel--research">
                <div className="admin-news__panel-head">
                  <div>
                    <span className="admin-news__eyebrow">Recherche</span>
                    <h2>GPT sucht</h2>
                    <p>Nach Begriff, Zeitraum und Relevanzlage recherchieren und Quellen zur Redaktion überführen.</p>
                  </div>
                </div>

                <form className="admin-news__search-form" onSubmit={handleResearchSubmit}>
                  <label>
                    <span>Begriffssuche</span>
                    <input
                      type="search"
                      value={researchTerm}
                      onChange={(event) => setResearchTerm(event.target.value)}
                      placeholder="z. B. Blue Card, Fachkräfte, Frankreich"
                    />
                  </label>

                  <label>
                    <span>Von</span>
                    <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
                  </label>

                  <label>
                    <span>Bis</span>
                    <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
                  </label>

                  <button type="submit" disabled={researchLoading || !researchTerm.trim()}>
                    {researchLoading ? "Suche läuft…" : "Recherche starten"}
                  </button>
                </form>

                <div className="admin-news__term-cloud">
                  <div className="admin-news__subhead">
                    <strong>Begriffswolke</strong>
                    <span>20 häufigste Begriffe</span>
                  </div>
                  <div className="admin-news__term-cloud-items">
                    {termCloud.map((term) => (
                      <button
                        key={term.term}
                        type="button"
                        style={{ fontSize: `${0.88 + Math.min(term.count, 10) * 0.09}rem` }}
                        onClick={() => setResearchTerm(term.term)}
                      >
                        {term.term}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="admin-news__results-head">
                  <div className="admin-news__subhead">
                    <strong>Gefundene Artikel</strong>
                    <span>{filteredResults.length} Treffer</span>
                  </div>
                  <button
                    type="button"
                    className="admin-news__ghost-button"
                    disabled={!selectedCount || savingAction === "create-ai"}
                    onClick={handleCreateAiArticle}
                  >
                    {savingAction === "create-ai" ? "Erstellt…" : `KI-Artikel aus Auswahl (${selectedCount})`}
                  </button>
                </div>

                <div className="admin-news__results">
                  {filteredResults.map((source) => {
                    const checked = selectedSources.some((entry) => entry.id === source.id);
                    return (
                      <article key={source.id} className="admin-news__result-card">
                        <label className="admin-news__result-select">
                          <input type="checkbox" checked={checked} onChange={() => toggleSourceSelection(source)} />
                          <span>für KI-Artikel vormerken</span>
                        </label>

                        <div className="admin-news__result-meta">
                          <span>{source.sourceName || "Quelle"}</span>
                          {source.publishedAt ? <span>{formatDate(source.publishedAt)}</span> : null}
                          {source.relevance ? <span>Relevanz {source.relevance}/5</span> : null}
                        </div>

                        <h3>{source.title}</h3>
                        <p>{source.summary}</p>
                        {source.note ? <small>{source.note}</small> : null}

                        <div className="admin-news__result-actions">
                          <a href={source.url} target="_blank" rel="noreferrer">
                            Quelle öffnen
                          </a>
                          <button
                            type="button"
                            disabled={Boolean(savingAction)}
                            onClick={() => handleSourceAction(source, "discard")}
                          >
                            verwerfen
                          </button>
                          <button
                            type="button"
                            disabled={Boolean(savingAction)}
                            onClick={() => handleSourceAction(source, "archive")}
                          >
                            archivieren
                          </button>
                          <button
                            type="button"
                            className="is-primary"
                            disabled={Boolean(savingAction)}
                            onClick={() => handleSourceAction(source, "ai")}
                          >
                            KI-Artikel
                          </button>
                        </div>
                      </article>
                    );
                  })}

                  {!filteredResults.length ? (
                    <div className="admin-news__empty">
                      <strong>Noch keine Rechercheergebnisse.</strong>
                      <span>Suche nach Begriff und Zeitraum, um aktuelle Nachrichtenquellen einzublenden.</span>
                    </div>
                  ) : null}
                </div>
              </section>

              <section className="admin-news__panel admin-news__panel--editor">
                <div className="admin-news__panel-head">
                  <div>
                    <span className="admin-news__eyebrow">Bearbeitung</span>
                    <h2>Redaktioneller Bericht</h2>
                    <p>Entwurf prüfen, Quellenlage lesen, Kategorie setzen und dann annehmen oder verwerfen.</p>
                  </div>
                </div>

                {editor ? (
                  <div className="admin-news__editor-stack">
                    <div className="admin-news__editor-form">
                      <label>
                        <span>Titel</span>
                        <input
                          type="text"
                          value={editor.title}
                          onChange={(event) => setEditor((current) => ({ ...current, title: event.target.value }))}
                        />
                      </label>

                      <label>
                        <span>Excerpt</span>
                        <textarea
                          rows={3}
                          value={editor.excerpt}
                          onChange={(event) => setEditor((current) => ({ ...current, excerpt: event.target.value }))}
                        />
                      </label>

                      <label>
                        <span>Bericht</span>
                        <textarea
                          rows={14}
                          value={editor.body}
                          onChange={(event) => setEditor((current) => ({ ...current, body: event.target.value }))}
                        />
                      </label>

                      <div className="admin-news__editor-meta">
                        <label>
                          <span>Kategorie</span>
                          <select
                            value={editor.category}
                            onChange={(event) => setEditor((current) => ({ ...current, category: event.target.value }))}
                          >
                            {categories.map((entry) => (
                              <option key={entry} value={entry}>
                                {entry}
                              </option>
                            ))}
                          </select>
                        </label>

                        <article>
                          <span>Status</span>
                          <strong>{editor.status}</strong>
                          <small>{editor.viewCount} Aufrufe</small>
                        </article>

                        <article>
                          <span>Quelle</span>
                          <strong>{editor.searchTerm || "manuell"}</strong>
                          <small>{editor.sourceCount} Quellen</small>
                        </article>
                      </div>

                      <div className="admin-news__editor-actions">
                        <button type="button" onClick={() => runArticleAction("save")} disabled={Boolean(savingAction)}>
                          ändern
                        </button>
                        <button type="button" onClick={() => runArticleAction("reject")} disabled={Boolean(savingAction)}>
                          ablehnen
                        </button>
                        <button type="button" onClick={() => runArticleAction("archive")} disabled={Boolean(savingAction)}>
                          archivieren
                        </button>
                        <button type="button" onClick={() => runArticleAction("regenerate")} disabled={Boolean(savingAction)}>
                          KI-neu
                        </button>
                        <button
                          type="button"
                          className="is-danger"
                          onClick={() => runArticleAction("delete")}
                          disabled={Boolean(savingAction)}
                        >
                          löschen
                        </button>
                        <button
                          type="button"
                          className="is-primary"
                          onClick={() => runArticleAction("accept")}
                          disabled={Boolean(savingAction) || !CATEGORY_OPTIONS.includes(editor.category)}
                        >
                          annehmen
                        </button>
                      </div>
                    </div>

                    <section className="admin-news__editor-sources">
                      <div className="admin-news__subhead">
                        <strong>Quellen</strong>
                        <span>werden im KI-Bericht genannt</span>
                      </div>
                      <div className="admin-news__source-list">
                        {editor.sourceList.map((source, index) => (
                          <article key={`${source.url || source.title || index}-${index}`} className="admin-news__source-card">
                            <strong>{source.title || `Quelle ${index + 1}`}</strong>
                            {source.sourceName ? <span>{source.sourceName}</span> : null}
                            {source.summary ? <p>{source.summary}</p> : null}
                            {source.url ? (
                              <a href={source.url} target="_blank" rel="noreferrer">
                                {source.url}
                              </a>
                            ) : null}
                          </article>
                        ))}

                        {!editor.sourceList.length ? (
                          <div className="admin-news__empty">
                            <strong>Keine Quellen hinterlegt.</strong>
                            <span>KI-Artikel und Rechercheimporte tragen ihre Quellen hier ein.</span>
                          </div>
                        ) : null}
                      </div>
                    </section>
                  </div>
                ) : (
                  <div className="admin-news__empty">
                    <strong>Noch kein Bericht ausgewählt.</strong>
                    <span>Wähle rechts einen bestehenden Bericht oder erzeuge links einen neuen KI-Artikel.</span>
                  </div>
                )}
              </section>
              <section className="admin-news__panel admin-news__panel--library">
                <div className="admin-news__panel-head">
                  <div>
                    <span className="admin-news__eyebrow">Redaktion</span>
                    <h2>Berichte und Entwürfe</h2>
                    <p>Veröffentlichen, archivieren, ablehnen und die Sichtbarkeit jedes Berichts verfolgen.</p>
                  </div>
                </div>

                <form className="admin-news__library-filter" onSubmit={handleArticleSearch}>
                  <input
                    type="search"
                    value={articleQuery}
                    onChange={(event) => setArticleQuery(event.target.value)}
                    placeholder="Berichte durchsuchen"
                  />
                  <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value || "all"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button type="submit" disabled={loading}>
                    {loading ? "Lädt…" : "Aktualisieren"}
                  </button>
                </form>

                <div className="admin-news__library">
                  {articles.map((article) => (
                    <button
                      key={article.id}
                      type="button"
                      className={`admin-news__article-card ${editor?.id === article.id ? "is-active" : ""}`.trim()}
                      onClick={() => setEditor(normalizeEditor(article))}
                    >
                      <div className="admin-news__article-topline">
                        <span>{article.category || "Alle"}</span>
                        <span>{article.status}</span>
                        <span>{Number(article.viewCount || 0)} Aufrufe</span>
                      </div>
                      <strong>{article.title}</strong>
                      <p>{article.excerpt}</p>
                      <small>
                        {article.isAiArticle ? "KI-Artikel" : "Redaktioneller Bericht"} ·{" "}
                        {article.updatedAt ? formatDate(article.updatedAt) : ""}
                      </small>
                    </button>
                  ))}

                  {!articles.length ? (
                    <div className="admin-news__empty">
                      <strong>Keine Berichte gefunden.</strong>
                      <span>Mit den Filtern oberhalb lässt sich die Redaktion gezielt eingrenzen.</span>
                    </div>
                  ) : null}
                </div>
              </section>
            </div>
          </section>
      </div>
    </AdminPageFrame>
  );
}
